import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { claimErrorText, formatDay, strikeApi } from "./rightsClaimApi";
import { AppealDialog } from "../artistStudio/TrackAppealDialog";
import { toast } from "react-toastify";

// Artist Studio > Rights & Legal > Strikes. Same table as the empty state in the design (Track, Reason, Date, Status);
// the banner above it counts the active strikes. Removed strikes stay listed with the reason they were removed.
export default function StrikeList({ columns, empty }) {
  const [state, setState] = useState({ loading: true, items: [], active: 0, next: null, error: "" });
  const [more, setMore] = useState(false);
  const [appealing, setAppealing] = useState(null); // the strike whose track is being appealed

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    strikeApi.mine({ limit: 50 }).then(
      (data) => setState({ loading: false, items: data?.items || [], active: data?.active_count || 0, next: data?.next_cursor || null, error: "" }),
      (e) => setState({ loading: false, items: [], active: 0, next: null, error: claimErrorText(e, "We couldn't load your strikes.") })
    );
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadMore = () => {
    setMore(true);
    strikeApi.mine({ limit: 50, cursor: state.next }).then(
      (data) => setState((s) => ({ ...s, items: [...s.items, ...(data?.items || [])], next: data?.next_cursor || null })),
      (e) => setState((s) => ({ ...s, error: claimErrorText(e, "We couldn't load more strikes.") }))
    ).finally(() => setMore(false));
  };

  const { loading, items, active, next, error } = state;
  const banner = active === 0
    ? { text: "0 active strikes on your account", cls: "bg-emerald-50 text-emerald-800" }
    : { text: `${active} active ${active === 1 ? "strike" : "strikes"} on your account`, cls: "bg-red-50 text-red-800" };

  return (
    <div className="space-y-4" data-testid="strike-list">
      {!loading && !error && <p className={`rounded-lg text-sm font-medium px-4 py-2.5 ${banner.cls}`} role="status">{banner.text}</p>}
      <div className="rounded-xl border border-gray-200 bg-[#f3f7f4] overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-xs font-bold uppercase tracking-wide" style={{ color: "#095228" }}>
            <tr>{columns.map((c) => <th key={c} className="px-5 py-3 whitespace-nowrap">{c}</th>)}</tr>
          </thead>
          <tbody className="bg-white">
            {loading && <tr className="border-t border-gray-200"><td colSpan={columns.length} className="px-5 py-4 text-gray-500">Loading…</td></tr>}
            {error && (
              <tr className="border-t border-gray-200"><td colSpan={columns.length} className="px-5 py-4 text-red-600" role="alert">{error} <button type="button" className="underline font-semibold" onClick={load}>Try again</button></td></tr>
            )}
            {!loading && !error && items.length === 0 && (
              <tr className="border-t border-gray-200"><td colSpan={columns.length} className="px-5 py-4 text-gray-500">{empty}</td></tr>
            )}
            {items.map((i) => (
              <tr key={i.id} className="border-t border-gray-200" data-testid={`strike-row-${i.id}`}>
                <td className="px-5 py-3">{i.track_id ? <Link to={`/sound/${i.track_id}`} className="font-semibold text-gray-900 hover:underline">{i.track_title || `Sound #${i.track_id}`}</Link> : <span className="text-gray-500">Your account</span>}</td>
                <td className="px-5 py-3">{i.reason}</td>
                <td className="px-5 py-3 whitespace-nowrap">{formatDay(i.issued_at)}</td>
                <td className="px-5 py-3">
                  {i.status === "active"
                    ? <span className="rounded-full bg-red-50 text-red-700 px-3 py-1 text-xs font-semibold">Active</span>
                    : <span className="rounded-full bg-gray-100 text-gray-700 px-3 py-1 text-xs font-semibold">Removed</span>}
                  {i.status === "removed" && (i.removed_reason || i.removed_at) && (
                    <span className="block text-xs text-gray-500 mt-1">{[i.removed_at ? formatDay(i.removed_at) : "", i.removed_reason].filter(Boolean).join(" · ")}</span>
                  )}
                  {i.status === "active" && i.appeal_status === "pending" && <span className="block text-xs text-gray-500 mt-1">Appeal under review</span>}
                  {i.status === "active" && i.appeal_status === "denied" && <span className="block text-xs text-gray-500 mt-1">Appeal denied</span>}
                  {i.status === "active" && i.track_id && !i.appeal_status && (
                    <button type="button" onClick={() => setAppealing(i)} className="mt-1 block text-xs font-semibold hover:underline" style={{ color: "#095228" }} data-testid={`strike-appeal-${i.id}`}>Appeal</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {appealing && (
        <AppealDialog
          track={{ id: appealing.track_id, title: appealing.track_title, rejection_reason: appealing.reason }}
          onCancel={() => setAppealing(null)}
          onSubmitted={() => { setAppealing(null); toast.success("Your appeal was sent. We will review it and update this strike."); load(); }}
        />
      )}
      {next && !error && (
        <button type="button" onClick={loadMore} disabled={more} className="text-sm font-semibold hover:underline disabled:opacity-60" style={{ color: "#095228" }}>{more ? "Loading…" : "Show more"}</button>
      )}
    </div>
  );
}
